import React, { useMemo, useState } from 'react';
import './template-body.scss';
import { TemplateDetailsBodyComponent } from '@zonofi/zono-money-store';
import { TouchableOpacity } from '@zonofi/seller-hub/common';

interface TemplateBodyProps {
  body: TemplateDetailsBodyComponent;
  isEdit?: boolean;
  values?: Record<string, string>;
  onEditVar?: (key: string, value: string) => void;
}

type Token =
  | { type: 'text'; value: string }
  | { type: 'variable'; value: string };

const tokenize = (text: string): Token[] => {
  if (!text) return [];

  const parts = text.split(/(\{\{.*?\}\})/g);

  return parts.filter(Boolean).map((part) => {
    if (part.startsWith('{{') && part.endsWith('}}')) {
      return {
        type: 'variable',
        value: part.slice(2, -2), // remove {{ }}
      };
    }
    return { type: 'text', value: part };
  });
};

export const TemplateBody: React.FC<TemplateBodyProps> = ({
  body,
  isEdit = false,
  values = {},
  onEditVar,
}) => {
  const tokens = useMemo(() => {
    return tokenize(body?.text ?? '');
  }, [body]);

  const replacements = body?.example?.body_text?.[0] || [];

  if (!tokens.length) return null;

  return (
    <>
      <div className="template-body">
        <div className="template-content">
          {tokens.map((token, index) => {
            // TEXT
            if (token.type === 'text') {
              return (
                <span
                  key={`text-${index}`}
                  className="template-text font-regular"
                >
                  {token.value}
                </span>
              );
            }

            // VARIABLE
            const variableIndex = Number(token.value) - 1;
            const mappedValue = replacements[variableIndex];
            const cleanValue = mappedValue?.replace(/[{}]/g, '') || '';
            const displayValue = values[cleanValue];

            // EDIT MODE (ONLY CUSTOM)
            if (isEdit && cleanValue === 'custom') {
              return (
                <TouchableOpacity
                  key={`var-${index}`}
                  onPress={() => onEditVar?.(cleanValue, token.value)}
                >
                  <span className="template-edit-text font-regular">
                    {displayValue
                      ? displayValue
                      : `{{${token.value}}} click to edit custom message`}
                  </span>
                </TouchableOpacity>
              );
            }

            // VIEW MODE
            return (
              <span
                key={`var-${index}`}
                className="template-variable font-regular"
              >
                {displayValue || cleanValue}
              </span>
            );
          })}
        </div>
      </div>
    </>
  );
};
